import {
  applyFilterWithManager,
  categoryMerchandising,
  type KlevuFetchFunctionReturnValue,
  type KlevuFetchModifer,
  KlevuSearchPreference,
  KlevuTypeOfRecord,
  sendMerchandisingViewEvent,
} from "@klevu/core";
import type { ProductShort } from "~/types/product";
import type { MappedKlevuResult } from "~/modules/klevu/types/MappedKlevuResult";
import { QueryParamKlevuFilterManager } from "~/modules/klevu/classes/QueryParamKlevuFilterManager";
import {
  calculatePagination,
  excludeListingHiddenRecords,
  prepareKlevuListFilters,
} from "~/modules/klevu/helpers";
import {
  applyKlevuSorting,
  calculateTotalPagesCount,
  toProductShortMapper,
} from "~/modules/catalog/helpers";
import { CATALOG_PAGE_PRODUCTS } from "~/modules/catalog/variables/CatalogFetchKeys";
import type { KlevuFilter } from "~/modules/klevu/types";
import { BASE_SEARCH_QUERY_PARAMS_SCHEMA } from "~/modules/catalog/variables/BaseSearchSchema";
import { ProductSortOptionType } from "~/modules/settings/types";
import { ZaprooKlevuFilterManager } from "~/modules/klevu/classes/ZaprooKlevuFilterManager";
import type { CategoryBreadcrumb } from "~/types/category/CategoryBreadcrumb";

export async function useKlevuCatalogPageData(
  slugs: ComputedRef<string[]>,
  category: Ref<any>,
  categoryBreadcrumbs: ComputedRef<CategoryBreadcrumb[]>,
  fetchCategories: () => Promise<void>,
) {
  const { fetch, mapQueryResult } = useKlevu();

  const {
    queryVariables: filters,
    committedQueryVariables,
    commitPush,
    reset,
  } = useQueryParams({
    ...BASE_SEARCH_QUERY_PARAMS_SCHEMA,
    sort: new QueryParamStr<ProductSortOptionType>().setDefault(
      ProductSortOptionType.top,
    ),
    filter: new QueryParamKlevuFilterManager(),
  });

  const isHeedFetchFiltersNextTime = ref<boolean>(true);

  const {
    data,
    pending,
    refresh,
    error: productFetchError,
  } = await useSyncCachedAsyncData(
    CATALOG_PAGE_PRODUCTS + "-" + slugs.value.join("-"),
    async () => {
      if (!category.value) await fetchCategories();

      if (!category.value) {
        throw new Error("Target category not found");
      }

      const searchModifiers = [
        applyFilterWithManager(
          committedQueryVariables.value.filter as ZaprooKlevuFilterManager,
        ) as KlevuFetchModifer,
        excludeListingHiddenRecords(),
        sendMerchandisingViewEvent(category.value.name),
      ];

      if (isHeedFetchFiltersNextTime.value) {
        searchModifiers.push(prepareKlevuListFilters(filters.filter));
      }

      try {
        const productsFetchQuery = categoryMerchandising(
          categoryBreadcrumbs.value.map((c) => c.category_name).join(";"),
          {
            id: "search",
            typeOfRecords: [KlevuTypeOfRecord.Product],
            searchPrefs: [KlevuSearchPreference.includeCategoryFilterInCatNav],
            ...calculatePagination(
              committedQueryVariables.value.page,
              committedQueryVariables.value.size,
            ),
          },
          ...searchModifiers,
        ) as KlevuFetchFunctionReturnValue;

        applyKlevuSorting(filters.sort, productsFetchQuery);
        await fetch(productsFetchQuery);

        return {
          products: mapQueryResult(
            "search",
            toProductShortMapper,
          ) as MappedKlevuResult<ProductShort>,
        };
      } finally {
        isHeedFetchFiltersNextTime.value = true;
      }
    },
    { immediate: true },
  );

  const productList = computed<ProductShort[]>(
    () => data.value?.products?.mappedRecords || [],
  );

  const totalResultsFound = computed(
    () => data.value?.products.meta.totalResultsFound || 0,
  );

  const isNoProducts = computed(() => !productList.value.length);

  const isProductsAndFiltersNoResults = computed(
    () => isNoProducts.value && !filters.filter.filters.length,
  );

  const selectedFilterCount = computed(
    () => filters.filter.selectedFilters().length,
  );

  const totalPages = computed<number>(() =>
    calculateTotalPagesCount(
      totalResultsFound.value,
      committedQueryVariables.value.size,
    ),
  );

  function onFilterSensitiveDataChange(): void {
    filters.page = 1;
    commitPush();
  }

  /** Restore filter state in FilterManager class from ssr hydration data **/
  if (import.meta.client && data.value?.products.filters) {
    filters.filter.initFromListFilters(
      (data.value?.products.filters as KlevuFilter[]) || [],
    );
  }

  /**
   * Re-label the committed (read) filter manager from the Klevu list filters so
   * the applied-filter chips show the human-readable label (e.g. "Maht (ml)")
   * instead of the raw attribute code (e.g. "capacity_ml").
   *
   * The committed manager is only populated from URL params and, unlike the
   * editable manager, isn't fed by the Klevu fetch modifier — so its filters
   * keep the raw attribute code as the label.
   */
  function syncCommittedFilterLabels(): void {
    const listFilters = data.value?.products.filters as
      | KlevuFilter[]
      | undefined;
    if (!listFilters?.length) {
      return;
    }
    (
      committedQueryVariables.value.filter as ZaprooKlevuFilterManager
    ).relabelFromListFilters(listFilters);
  }

  // Apply on setup (SSR renders the chips) and whenever the results change
  // (filtering/paging) or the committed query is rebuilt from a URL change.
  watch([data, committedQueryVariables], syncCommittedFilterLabels, {
    immediate: true,
  });

  return {
    filters,
    committedQueryVariables,
    commitPush,
    reset,
    data,
    pending,
    refresh,
    refreshPage: refresh,
    productFetchError,
    error: productFetchError,
    productList,
    totalResultsFound,
    totalPages,
    selectedFilterCount,
    isNoProducts,
    isProductsAndFiltersNoResults,
    onFilterSensitiveDataChange,
  };
}
